/*
 * Builds `help` output and tab-completion candidates from the manifest.
 */
import {commands, matchCommand, ShellCommand, unsupportedReason} from "./ShellManifest";

/** Command description, suffixed with "(pro)" for enterprise-only commands. */
function describe(c: ShellCommand): string {
    if (!c.enterprise) {
        return c.description;
    }
    return c.description ? `${c.description} (pro)` : "(pro)";
}

/** `help` with no args: all commands grouped by their group. */
export function helpOverview(): string {
    const byGroup = new Map<string, ShellCommand[]>();
    for (const c of commands) {
        const g = c.group || "Other";
        if (!byGroup.has(g)) {
            byGroup.set(g, []);
        }
        byGroup.get(g)!.push(c);
    }
    const lines: string[] = ["Available commands (type `help <command>` for details):", ""];
    for (const group of [...byGroup.keys()].sort()) {
        lines.push(`${group}:`);
        for (const c of byGroup.get(group)!.sort((a, b) => a.words.join(" ").localeCompare(b.words.join(" ")))) {
            const flag = c.unsupported ? "  (unavailable)" : "";
            lines.push(`  ${c.words.join(" ").padEnd(40)} ${describe(c)}${flag}`);
        }
        lines.push("");
    }
    return lines.join("\n");
}

/** `help <command words>`: description + option table for one command. */
export function helpCommand(tokens: string[]): string {
    const match = matchCommand(tokens);
    if (!match) {
        return `unknown command: ${tokens.join(" ")}`;
    }
    const c = match.command;
    const desc = describe(c);
    const lines: string[] = [c.words.join(" "), desc ? `  ${desc}` : ""];
    if (c.unsupported) {
        lines.push(`  NOTE: this command ${unsupportedReason(c.unsupported)}.`);
    }
    if (c.options.length > 0) {
        lines.push("", "Options:");
        for (const o of c.options) {
            const bits: string[] = [o.type];
            if (o.type === "file") {
                bits.push("chosen via file dialog");
            }
            if (o.required) {
                bits.push("required");
            }
            if (o.default !== undefined) {
                bits.push(`default=${o.default}`);
            }
            if (o.enumValues) {
                bits.push(`values=[${o.enumValues.join(", ")}]`);
            }
            lines.push(`  --${o.name.padEnd(24)} ${bits.join(", ")}`);
        }
    }
    return lines.filter(l => l !== "").join("\n");
}

interface Context {
    tokens: string[];
    idx: number;       // index of the word currently being completed
    partial: string;   // text of that word so far ("" when the line ends in a space)
    before: string[];  // settled tokens before the current word
    trailingSpace: boolean;
}

function context(line: string): Context {
    const trailingSpace = /\s$/.test(line);
    const tokens = line.split(/\s+/).filter(Boolean);
    const idx = trailingSpace ? tokens.length : Math.max(0, tokens.length - 1);
    const partial = trailingSpace ? "" : (tokens[idx] ?? "");
    const before = tokens.slice(0, idx);
    return {tokens, idx, partial, before, trailingSpace};
}

/** The command formed by the leading words of `tokens` (ignoring any trailing options). */
function leadingCommand(tokens: string[]) {
    return matchCommand(tokens)?.command ?? null;
}

function commonPrefix(values: string[]): string {
    if (values.length === 0) {
        return "";
    }
    let prefix = values[0];
    for (const v of values.slice(1)) {
        while (!v.startsWith(prefix)) {
            prefix = prefix.slice(0, -1);
        }
    }
    return prefix;
}

function rebuild(before: string[], word: string): string {
    return (before.length > 0 ? before.join(" ") + " " : "") + word;
}

/** Candidate completions for the word currently being typed. */
function candidatesFor(ctx: Context): string[] {
    const {idx, partial, before, tokens, trailingSpace} = ctx;

    // value completion for an enum option: previous token is `--enumOption`
    const prev = trailingSpace ? tokens[tokens.length - 1] : tokens[idx - 1];
    if (prev && prev.startsWith("--")) {
        const cmd = leadingCommand(before);
        const opt = cmd?.options.find(o => o.name === prev.slice(2));
        if (opt && opt.type === "enum" && opt.enumValues) {
            return opt.enumValues.filter(e => e.toLowerCase().startsWith(partial.toLowerCase()));
        }
    }

    // option-name completion
    if (partial.startsWith("--")) {
        const cmd = leadingCommand(before);
        if (!cmd) {
            return [];
        }
        const p = partial.slice(2);
        return cmd.options.filter(o => o.name.startsWith(p)).map(o => `--${o.name}`);
    }

    // command-word completion at this position
    const words = new Set<string>();
    for (const c of commands) {
        if (c.words.length > idx && before.every((w, i) => c.words[i] === w) && c.words[idx].startsWith(partial)) {
            words.add(c.words[idx]);
        }
    }
    if (words.size > 0) {
        return [...words].sort();
    }

    // no further command words: if the line is already a complete command, offer its options
    const cmd = leadingCommand(before);
    return cmd ? cmd.options.map(o => `--${o.name}`) : [];
}

/**
 * Tab-completion for a line. Returns either a new line to set (`completion`) or a list of
 * candidates to display (`listing`). Mirrors shell behaviour: complete to the common prefix,
 * advance past an already-complete word, and list when the choice is ambiguous.
 */
export function autocomplete(line: string): {completion: string | null; listing: string[]} {
    const ctx = context(line);
    const candidates = candidatesFor(ctx);

    if (candidates.length === 0) {
        return {completion: null, listing: []};
    }
    if (candidates.length === 1) {
        const only = candidates[0];
        if (only === ctx.partial) {
            // word already complete: advance so the next Tab completes the following word
            return {completion: ctx.trailingSpace ? line : line + " ", listing: []};
        }
        return {completion: rebuild(ctx.before, only) + " ", listing: []};
    }
    const lcp = commonPrefix(candidates);
    if (lcp.length > ctx.partial.length) {
        return {completion: rebuild(ctx.before, lcp), listing: []};
    }
    return {completion: null, listing: candidates};
}
